import React from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Facebook, 
  Instagram, 
  FileText, 
  Shield, 
  ExternalLink, 
  Navigation, 
  Clock 
} from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';
import { AGENCY_CONFIG } from '../config/agency';
interface FooterProps {
  onNavigate: (tab: string) => void;
  onSelectNeighborhood?: (neighborhood: string) => void;
}

export default function Footer({ onNavigate, onSelectNeighborhood }: FooterProps) {
  const { content } = useSiteContent();
  const f = content.footer;
  const branding = content.branding;
  const nav = content.navigation;

  const mapAddress = f.mapSection?.address || nav.topBar.addressText || AGENCY_CONFIG.address;
  const mapQuery = mapAddress.includes('Генерал Колев') ? 'Варна, бул. Генерал Колев 113' : mapAddress;
  const mapPhone = f.mapSection?.phone || nav.topBar.phone || AGENCY_CONFIG.phone;

  return (
    <>
      {/* Office Location & Interactive Google Maps Section (Above the Footer) */}
      {f.mapSection?.enabled !== false && (
        <section id="office-location-maps" className="border-t border-slate-800/90 bg-[#081322] py-12 md:py-16 text-slate-300 font-sans">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
              
              {/* Left Column: Office Details Card */}
              <div className="lg:col-span-5 bg-[#122338]/95 border border-slate-700/70 rounded-2xl p-6 sm:p-7 flex flex-col justify-between shadow-2xl">
                <div>
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold tracking-widest uppercase bg-[#C5A46E]/15 text-[#C5A46E] border border-[#C5A46E]/30">
                      <MapPin className="w-3 h-3 text-[#C5A46E]" />
                      {f.mapSection?.badge || 'ОФИС И ЛОКАЦИЯ'}
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">
                    {f.mapSection?.title || 'Централен офис Варна'}
                  </h3>
                  
                  <p className="text-xs text-slate-300 mt-2 leading-relaxed font-light">
                    {f.mapSection?.subtitle || 'Заповядайте на място за лична консултация, обсъждане на оферти или подписване на документи.'}
                  </p>

                  <div className="space-y-3.5 mt-5 pt-5 border-t border-slate-700/60 text-xs">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-[#C5A46E] shrink-0 mt-0.5" />
                      <div>
                        <span className="block text-[11px] font-bold text-white uppercase tracking-wider">Адрес на офиса</span>
                        <span className="text-slate-200 text-xs mt-0.5 block font-medium">{mapAddress}</span>
                        {f.mapSection?.landmark && (
                          <span className="text-[11px] text-[#C5A46E] font-medium block mt-1">
                            📍 {f.mapSection.landmark}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-[#C5A46E] shrink-0 mt-0.5" />
                      <div>
                        <span className="block text-[11px] font-bold text-white uppercase tracking-wider">Работно време</span>
                        <span className="text-slate-300 text-xs mt-0.5 block font-light">
                          {f.mapSection?.workingHours || 'Пон – Пет: 09:00 – 18:00 ч. | Събота: 10:00 – 14:00 ч.'}
                        </span>
                      </div>
                    </div>

                    {mapPhone && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-4 h-4 text-[#C5A46E] shrink-0 mt-0.5" />
                        <div>
                          <span className="block text-[11px] font-bold text-white uppercase tracking-wider">Директен телефон</span>
                          <a 
                            href={`tel:${mapPhone.replace(/[^\d+]/g, '')}`} 
                            className="text-slate-200 hover:text-[#C5A46E] transition-colors text-xs font-semibold mt-0.5 block"
                          >
                            {mapPhone}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Direct Google Maps Action Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 pt-5 border-t border-slate-700/60">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#C5A46E] hover:bg-[#B1905E] text-[#0F1E32] font-bold text-xs rounded-xl transition shadow cursor-pointer uppercase tracking-wider"
                  >
                    <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                    <span>Google Maps</span>
                  </a>

                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapQuery)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1B324E] hover:bg-slate-700 text-white font-bold text-xs rounded-xl transition border border-slate-600/70 shadow cursor-pointer uppercase tracking-wider"
                  >
                    <Navigation className="w-3.5 h-3.5 text-[#C5A46E] shrink-0" />
                    <span>Упътване</span>
                  </a>
                </div>
              </div>

              {/* Right Column: Embedded Interactive Google Map */}
              <div className="lg:col-span-7 bg-[#122338]/95 border border-slate-700/70 rounded-2xl p-2.5 flex flex-col shadow-2xl overflow-hidden min-h-[300px] lg:min-h-[380px]">
                <div className="relative w-full h-full min-h-[290px] lg:min-h-[360px] rounded-xl overflow-hidden border border-slate-700/50 bg-[#0F1E32]">
                  <iframe
                    title="Google Maps - Офис Варна Имотен Център"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                    className="w-full h-full min-h-[290px] lg:min-h-[360px] border-0 block"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="absolute top-2.5 left-2.5 bg-[#0F1E32]/95 backdrop-blur-sm border border-slate-700/80 rounded-lg px-3 py-1.5 shadow-lg flex items-center gap-2 pointer-events-none">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-[11px] font-bold text-white tracking-wide">
                      гр. Варна, бул. Генерал Колев 113
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Main Footer */}
      <footer className="bg-[#0D1B2E] text-slate-300 font-sans border-t border-slate-800">
        {/* Upper footer content */}
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Brand about text */}
          <div className="flex flex-col gap-4">
            <div className="inline-block">
              {/* Crisp white window tightly fitted to logo height */}
              <div className="inline-flex items-center justify-center bg-white rounded-lg px-3 py-1 shadow-md border border-white/20">
                <img 
                  src={branding.logoUrl ? branding.logoUrl : "/imotencenterlogo-cropped.png"} 
                  alt={branding.logoAlt || branding.siteTitle} 
                  className="h-14 sm:h-16 md:h-20 w-auto object-contain block"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed font-light mt-1">
              {f.aboutText || 'Вашият доверен партньор на пазара на имоти във Варна.'}
            </p>
            <div className="flex flex-col gap-2.5 mt-2 text-sm text-slate-400">
              {nav.topBar.addressText && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#C5A46E] shrink-0 mt-0.5" />
                  <span>{nav.topBar.addressText}</span>
                </div>
              )}
              {nav.topBar.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#C5A46E] shrink-0" />
                  <a href={`tel:${nav.topBar.phone.replace(/[^\d+]/g, '')}`} className="hover:text-[#C5A46E] transition-colors">{nav.topBar.phone}</a>
                </div>
              )}
              {nav.topBar.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#C5A46E] shrink-0" />
                  <a href={`mailto:${nav.topBar.email}`} className="hover:text-[#C5A46E] transition-colors">{nav.topBar.email}</a>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm tracking-widest uppercase mb-5 pb-2 border-b-2 border-[#C5A46E]/30 w-16">
              {f.quickLinksTitle}
            </h4>
            <ul className="space-y-3 text-sm">
              {f.quickLinks.map((link) => {
                const tabMap: Record<string, string> = {
                  home: '/',
                  sales: '/prodazhbi',
                  rentals: '/naemi',
                  services: '/uslugi',
                  team: '/ekip',
                  about: '/za-nas',
                  contacts: '/kontakti',
                  all: '/oferti'
                };
                const href = tabMap[link.tab || 'home'] || '/';
                return (
                  <li key={link.id}>
                    <a 
                      href={href}
                      onClick={(e) => { e.preventDefault(); onNavigate(link.tab || 'home'); }} 
                      className="hover:text-[#C5A46E] text-slate-400 transition-colors focus:outline-none cursor-pointer block"
                    >
                      {link.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Popular Locations */}
          <div>
            <h4 className="text-white font-semibold text-sm tracking-widest uppercase mb-5 pb-2 border-b-2 border-[#C5A46E]/30 w-16">
              {f.popularLocationsTitle}
            </h4>
            <ul className="space-y-3 text-sm text-slate-400">
              {f.popularLocations.map((loc) => (
                <li key={loc.id}>
                  <button 
                    type="button"
                    onClick={() => {
                      if (onSelectNeighborhood) {
                        onSelectNeighborhood(loc.neighborhood);
                      } else {
                        onNavigate('all');
                      }
                    }} 
                    className="hover:text-[#C5A46E] hover:translate-x-1 transition-all text-left focus:outline-none cursor-pointer flex items-center gap-1.5 group"
                    title={`Виж всички имоти в ${loc.neighborhood}`}
                  >
                    <span className="text-[#C5A46E] text-xs transition-transform group-hover:translate-x-0.5">›</span>
                    <span>{loc.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services offerings list */}
          <div>
            <h4 className="text-white font-semibold text-sm tracking-widest uppercase mb-5 pb-2 border-b-2 border-[#C5A46E]/30 w-16">
              {f.servicesTitle}
            </h4>
            <ul className="space-y-3 text-sm text-slate-400">
              {f.servicesList.map((svc) => (
                <li key={svc.id}>
                  <button 
                    onClick={() => onNavigate('services')} 
                    className="hover:text-white transition-colors text-left focus:outline-none cursor-pointer"
                  >
                    {svc.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social and Newsletter Bottom Grid */}
        <div className="max-w-7xl mx-auto px-4 py-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              {nav.topBar.facebookUrl && (
                <a 
                  href={nav.topBar.facebookUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Facebook страница на Варна Имотен Център"
                  className="bg-[#15253A] w-11 h-11 rounded-lg flex items-center justify-center text-slate-300 hover:text-white hover:bg-amber-500 transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {nav.topBar.instagramUrl && (
                <a 
                  href={nav.topBar.instagramUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Instagram профил на Варна Имотен Център"
                  className="bg-[#15253A] w-11 h-11 rounded-lg flex items-center justify-center text-slate-300 hover:text-white hover:bg-amber-500 transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}
            </div>
            <span>{f.creditsText}</span>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <button
              type="button"
              onClick={() => onNavigate('privacy')}
              className="flex items-center gap-1.5 text-slate-300 hover:text-[#C5A46E] transition-colors cursor-pointer text-xs"
            >
              <Shield className="w-3.5 h-3.5 text-[#C5A46E]" />
              <span className="hover:underline">{f.privacyText || 'Политика за поверителност'}</span>
            </button>
            <button
              type="button"
              onClick={() => onNavigate('terms')}
              className="flex items-center gap-1.5 text-slate-300 hover:text-[#C5A46E] transition-colors cursor-pointer text-xs"
            >
              <FileText className="w-3.5 h-3.5 text-[#C5A46E]" />
              <span className="hover:underline">{f.termsText || 'Общи условия'}</span>
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('open_cookie_settings'))}
              className="flex items-center gap-1 text-slate-300 hover:text-[#C5A46E] transition-colors cursor-pointer text-xs"
              title="Настройки за бисквитки & Поверителност (GDPR)"
            >
              <span>🍪</span>
              <span className="underline decoration-slate-600 underline-offset-2 hover:decoration-[#C5A46E]">Бисквитки & GDPR</span>
            </button>
          </div>
        </div>

        {/* Extreme bottom developer copyright */}
        <div className="bg-[#0A1422] text-center text-xs text-slate-400 py-3.5 border-t border-slate-900">
          {f.copyrightText}
        </div>
      </footer>
    </>
  );
}
